import useConfigValues from '../../ReduxFunctions/useConfigValues';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchData } from '../../Helpers/externapi';
import { setHospitalImage } from '../../ReduxFunctions/ReduxSlice';
import { logToCloudWatch } from '../../Helpers/cloudwatchLogger';

const OTP_LENGTH = 6;
const MOBILE_NUMBER_LENGTH = 10;
const CARD_NUMBER_LENGTH = 12;
const OTP_TIMEOUT_SECONDS = 120;
const MEMBER_SESSION_TIMEOUT_MS = 10 * 60 * 1000;

const createEmptyOtp = () => new Array(OTP_LENGTH).fill('');

const formatCardNumber = (value) => {
    return value
        .replace(/\D/g, '')
        .slice(0, CARD_NUMBER_LENGTH)
        .match(/.{1,4}/g)
        ?.join(' ') || '';
};

const normalizeCardNumber = (value) => value.replace(/\s/g, '');

const useVerifyMemberController = () => {
    const configValues = useConfigValues('hospitalImagesURL', 'CardFront', 'CardBack', 'LogoWithoutName');
    const hospitalImage = useSelector((state) => state.hospitalImage);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const hospitalName = sessionStorage.getItem('hospitalName');
    const hospitalLogo = sessionStorage.getItem('hospitalImage');
    const hospitalId = sessionStorage.getItem('hospitalId');

    const [mobileNumber, setMobileNumber] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [disableOtp, setDisableOtp] = useState(true);
    const [disableVerify, setDisableVerify] = useState(true);
    const [timeLeft, setTimeLeft] = useState(OTP_TIMEOUT_SECONDS);
    const [isRunning, setIsRunning] = useState(false);
    const [numberError, setNumberError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [guid, setGuid] = useState();
    const [otp, setOtp] = useState(createEmptyOtp());
    const [otpLoading, setOtpLoading] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [isFlipped, setIsFlipped] = useState(false);
    const [remainingOtp, setRemainingOtp] = useState();
    const [frontCard, setFrontCard] = useState();
    const [backCard, setBackCard] = useState();
    const [logo, setLogo] = useState();

    const inputsRef = useRef([]);

    const logGroupName = process.env.REACT_APP_LOGGER;
    const logStreamName = `${hospitalId} (${hospitalName})-${new Date().toISOString().split('T')[0]}`;

    const resetOtpState = () => {
        setOtp(createEmptyOtp());
        setOtpError('');
        setDisableVerify(true);
    };

    const syncOtpAvailability = (nextMobileNumber, nextCardNumber, runningState = isRunning) => {
        const canSendOtp = !runningState && (
            nextMobileNumber.length === MOBILE_NUMBER_LENGTH ||
            normalizeCardNumber(nextCardNumber).length === CARD_NUMBER_LENGTH
        );

        setDisableOtp(!canSendOtp);
    };

    useEffect(() => {
        if (!configValues.length) {
            return;
        }

        if (!hospitalImage.length) {
            const imageUrl = configValues.find((val) => val.ConfigKey === 'hospitalImagesURL');
            if (imageUrl && hospitalLogo) {
                dispatch(setHospitalImage(imageUrl.ConfigValue + hospitalLogo));
            }
        }

        const cardFront = configValues.find((val) => val.ConfigKey === 'CardFront');
        const cardBack = configValues.find((val) => val.ConfigKey === 'CardBack');
        const ohoLogo = configValues.find((val) => val.ConfigKey === 'LogoWithoutName');

        setFrontCard(cardFront);
        setBackCard(cardBack);
        setLogo(ohoLogo);
    }, [configValues, dispatch, hospitalImage, hospitalLogo]);

    useEffect(() => {
        if (!isRunning) {
            return undefined;
        }

        if (timeLeft === 0) {
            setIsRunning(false);
            resetOtpState();

            const canSendOtp =
                mobileNumber.length === MOBILE_NUMBER_LENGTH ||
                normalizeCardNumber(cardNumber).length === CARD_NUMBER_LENGTH;

            setDisableOtp(!canSendOtp);
            return undefined;
        }

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [cardNumber, isRunning, mobileNumber, timeLeft]);

    const handleChange = (value, index) => {
        if (Number.isNaN(Number(value)) || value.length > 1) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        const filledOtp = newOtp.join('');

        setOtp(newOtp);
        setDisableVerify(filledOtp.length < OTP_LENGTH);

        if (value && index < OTP_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (event, index) => {
        if (event.key !== 'Backspace') {
            return;
        }

        const newOtp = [...otp];

        if (newOtp[index]) {
            newOtp[index] = '';
            setOtp(newOtp);
            setDisableVerify(true);
            return;
        }

        if (index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

        if (!pastedData) {
            return;
        }

        const newOtp = createEmptyOtp();
        pastedData.split('').forEach((char, index) => {
            newOtp[index] = char;
        });

        setOtp(newOtp);
        setDisableVerify(pastedData.length !== OTP_LENGTH);

        const firstEmptyIndex = newOtp.indexOf('');
        inputsRef.current[firstEmptyIndex !== -1 ? firstEmptyIndex : OTP_LENGTH - 1]?.focus();
    };

    const buildOtpPayload = () => {
        const normalizedCard = normalizeCardNumber(cardNumber);

        if (normalizedCard.length === CARD_NUMBER_LENGTH) {
            return {
                payload: { cardNumber: normalizedCard },
                logDetails: { cardNumber: normalizedCard },
            };
        }

        if (mobileNumber.length === MOBILE_NUMBER_LENGTH) {
            return {
                payload: { mobileNumber },
                logDetails: { mobileNumber },
            };
        }

        return null;
    };

    const handleOtpSuccess = async (otpResponse, logDetails) => {
        await logToCloudWatch(logGroupName, logStreamName, {
            event: 'OTP Sent Successfully',
            details: logDetails,
        });

        setTimeLeft(OTP_TIMEOUT_SECONDS);
        setIsRunning(true);
        setNumberError('');
        setDisableOtp(true);
        setGuid(otpResponse.guid);
        setMobileNumber(otpResponse.mobileNumber);
        setRemainingOtp(otpResponse.remainingAttempts);
        setIsOtpSent(true);
    };

    const handleOtpFailure = async (otpResponse, payload) => {
        await logToCloudWatch(logGroupName, logStreamName, {
            event: 'Failed to send OTP -OHOCards/CardNumberorMobileNoVerification',
            payload,
            response: otpResponse,
        });

        setIsRunning(false);
        setNumberError(otpResponse?.message || 'Sorry, something went wrong. Please contact support team.');
    };

    const handleOtpSent = async () => {
        const request = buildOtpPayload();
        if (!request) {
            return;
        }

        try {
            setOtpLoading(true);

            const otpResponse = await fetchData('lambdaAPI/OHOCards/CardNumberorMobileNoVerification', request.payload);

            if (otpResponse?.jwtToken) {
                localStorage.setItem('token', otpResponse.jwtToken);
            }

            if (otpResponse?.status) {
                await handleOtpSuccess(otpResponse, request.logDetails);
                return;
            }

            await handleOtpFailure(otpResponse, request.payload);
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifySuccess = async (verifyResponse, payload) => {
        const expirationTime = Date.now() + MEMBER_SESSION_TIMEOUT_MS;

        await logToCloudWatch(logGroupName, logStreamName, {
            event: 'OTP Verified Successfully',
            details: payload,
        });

        setIsOtpSent(false);
        sessionStorage.setItem('memberId', verifyResponse.memberId);
        sessionStorage.setItem('memberTime', expirationTime);

        navigate('/bookconsultation', {
            replace: true,
            state: { memberId: verifyResponse.memberId },
        });
    };

    const handleVerifyFailure = async (verifyResponse, payload) => {
        await logToCloudWatch(logGroupName, logStreamName, {
            event: 'Failed to verify OTP',
            details: payload,
            response: verifyResponse,
        });

        setOtpError(verifyResponse?.msg || 'Unable to verify OTP.');
    };

    const handleVerify = async (event) => {
        event.preventDefault();

        const request = buildOtpPayload();
        if (!request) {
            return;
        }

        const verificationPayload = {
            ...request.payload,
            otpGenerated: otp.join(''),
            guid,
        };

        try {
            setVerifyLoading(true);

            const verifyResponse = await fetchData('lambdaAPI/Customer/OTPValidation', verificationPayload);

            if (verifyResponse?.status) {
                await handleVerifySuccess(verifyResponse, verificationPayload);
                return;
            }

            await handleVerifyFailure(verifyResponse, verificationPayload);
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleMobileNumberChange = (value) => {
        const normalizedValue = value.replace(/\D/g, '').slice(0, MOBILE_NUMBER_LENGTH);
        setMobileNumber(normalizedValue);
        setCardNumber('');
        setNumberError('');
        syncOtpAvailability(normalizedValue, '');
    };

    const handleCardNumberChange = (value) => {
        const formattedValue = formatCardNumber(value);
        setCardNumber(formattedValue);
        setMobileNumber('');
        setNumberError('');
        syncOtpAvailability('', formattedValue);
    };

    const onChangeInput = (event) => {
        event.preventDefault();

        const { id, value } = event.target;

        if (id === 'mobileNumber') {
            handleMobileNumberChange(value);
            return;
        }

        if (id === 'cardNumber') {
            handleCardNumberChange(value);
        }
    };

    const backFromOtp = () => {
        const isConfirmed = window.confirm('Are you sure, You want to go back for Member Verification?');
        if (!isConfirmed) {
            return;
        }

        setIsOtpSent(false);
        setIsRunning(false);
        setMobileNumber('');
        setCardNumber('');
        setNumberError('');
        resetOtpState();
        setDisableOtp(true);
    };

    const toggleCardFlip = () => {
        setIsFlipped((prevState) => !prevState);
    };

    const goToDashboard = () => {
        navigate('/dashboard');
    };

    return {
        hospitalName,
        hospitalLogo,
        hospitalImage,
        mobileNumber,
        cardNumber,
        disableOtp,
        disableVerify,
        timeLeft,
        numberError,
        otpError,
        isOtpSent,
        otp,
        otpLoading,
        verifyLoading,
        isFlipped,
        remainingOtp,
        frontCard,
        backCard,
        logo,
        inputsRef,
        handleChange,
        handleKeyDown,
        handlePaste,
        handleOtpSent,
        handleVerify,
        onChangeInput,
        backFromOtp,
        toggleCardFlip,
        goToDashboard,
    };
};

export default useVerifyMemberController;
